import { ModuleDocContent } from "./docsData";

export const settingsModuleDocs: Record<string, ModuleDocContent> = {
  "business-settings": {
    title: "Business Settings",
    subtitle: "Platform Configuration",
    color: "slate",
    badge: "Settings",
    description:
      "Configure your organization's company information, brand assets, self-registration, trending keywords, and notification preferences from a single settings page.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Business Settings</strong> is a single-page form where you manage company information, sidebar logos, self-registration, trending topic keywords, and organizational email/ticket notification preferences. Changes apply platform-wide after clicking <strong>Save Settings</strong>.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/003df6a0-f11a-4bf5-a2f9-f3703131cc82.png" alt="Business Settings" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Business Settings configuration page</p></div>

      <section>
        <h2>How it works</h2>
        <div class="sz-steps">
          <div class="sz-step">
            <div class="sz-step-num">1</div>
            <div>
              <div class="sz-step-title">Open Business Settings</div>
              <div class="sz-step-desc">
                Navigate to <strong>Settings &gt; Business Settings</strong> from the sidebar. The page loads your current configuration.
              </div>
            </div>
          </div>
          <div class="sz-step">
            <div class="sz-step-num">2</div>
            <div>
              <div class="sz-step-title">Edit the settings you need</div>
              <div class="sz-step-desc">
                Update company info, upload logos, toggle self-registration, add trending keywords, or configure notification preferences across the four sections on the page.
              </div>
            </div>
          </div>
          <div class="sz-step">
            <div class="sz-step-num">3</div>
            <div>
              <div class="sz-step-title">Save changes</div>
              <div class="sz-step-desc">
                Click <strong>Save Settings</strong> at the bottom. All changes are applied platform-wide immediately.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h2>Company Information</h2>
        <p>Basic organizational details used across the platform.</p>
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Company Name</strong></td>
              <td>Text (required)</td>
              <td>Your organization name displayed in headers, emails, and certificates.</td>
            </tr>
            <tr>
              <td><strong>Contact Email</strong></td>
              <td>Email (required)</td>
              <td>Primary contact email for the organization.</td>
            </tr>
            <tr>
              <td><strong>Phone Number</strong></td>
              <td>International phone input</td>
              <td>Organization phone number with country code selector.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Brand Assets</h2>
        <p>Upload logos that appear in the sidebar navigation.</p>
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Expanded Menu Logo</strong></td>
              <td>Displayed when the sidebar is expanded. Image upload, max 10 MB. Click <strong>View</strong> to preview the current logo in a full-size modal.</td>
            </tr>
            <tr>
              <td><strong>Collapsed Menu Logo</strong></td>
              <td>Compact icon displayed when the sidebar is collapsed. Image upload, max 10 MB. Click <strong>View</strong> to preview.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>General Settings</h2>
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Allow User Self-Registration</strong></td>
              <td>Checkbox</td>
              <td>When enabled, users can create their own accounts without an admin invitation.</td>
            </tr>
            <tr>
              <td><strong>Trending Topic Keywords</strong></td>
              <td>Tags input</td>
              <td>Add keywords (press Enter to add) to identify and track trending topics in your content. These keywords feed the Trending Topics discovery module.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Organization Email Notifications</h2>
        <p>Control email notifications sent to organizational hierarchy members when learning events occur.</p>
        <table>
          <thead>
            <tr>
              <th>Setting</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Enable Organizational Email Notifications</strong></td>
              <td>Toggle</td>
              <td>Master switch. When enabled, reveals checkboxes for which hierarchy members receive emails.</td>
            </tr>
            <tr>
              <td><strong>Notify BU Head</strong></td>
              <td>Checkbox</td>
              <td>Send email notifications to the Business Unit Head.</td>
            </tr>
            <tr>
              <td><strong>Notify Reporting Manager</strong></td>
              <td>Checkbox</td>
              <td>Send email notifications to the Reporting Manager.</td>
            </tr>
            <tr>
              <td><strong>Notify HRBP</strong></td>
              <td>Checkbox</td>
              <td>Send email notifications to the HR Business Partner.</td>
            </tr>
          </tbody>
        </table>
        <div class="sz-callout sz-callout-tip">
          <div class="sz-callout-label">Note</div>
          <p>At least one recipient must be selected when organizational notifications are enabled.</p>
        </div>
      </section>

      <section>
        <h2>Organization Ticket Notification</h2>
        <p>Configure self-ticket assignment and define who can approve self-assigned tickets.</p>
        <table>
          <thead>
            <tr>
              <th>Setting</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Enable Self Ticket Assignment</strong></td>
              <td>Toggle</td>
              <td>Allow users to self-assign tickets. When enabled, reveals approver checkboxes below.</td>
            </tr>
            <tr>
              <td><strong>BU Head</strong></td>
              <td>Checkbox</td>
              <td>Allow BU Head to approve self-assigned tickets.</td>
            </tr>
            <tr>
              <td><strong>Reporting Manager</strong></td>
              <td>Checkbox</td>
              <td>Allow Reporting Manager to approve self-assigned tickets.</td>
            </tr>
            <tr>
              <td><strong>HRBP</strong></td>
              <td>Checkbox</td>
              <td>Allow HR Business Partner to approve self-assigned tickets.</td>
            </tr>
            <tr>
              <td><strong>All Admins</strong></td>
              <td>Checkbox</td>
              <td>Allow all administrators to approve self-assigned tickets.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Initial platform setup</div>
            <div class="sz-use-case-flow">
              Enter company name, contact email, and phone number. Upload expanded and collapsed sidebar logos. Enable self-registration if open enrollment is desired. Click Save Settings.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Configure notification hierarchy</div>
            <div class="sz-use-case-flow">
              Enable organizational email notifications and check the BU Head and Reporting Manager boxes so they receive automated emails when their team members complete courses or miss deadlines.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Enable self-service ticket requests</div>
            <div class="sz-use-case-flow">
              Turn on Self Ticket Assignment and select which approvers (BU Head, HRBP, or All Admins) can approve learner self-assignment requests for courses, certifications, and learning paths.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Pro tip</div>
        <p>Upload both expanded and collapsed logos so your branding looks polished regardless of the sidebar state. Click <strong>View</strong> on each logo field to preview before saving.</p>
      </div>

    </div>`,
  },

  "ticket-requests": {
    title: "Ticket Requests",
    subtitle: "Self-Assignment Request Management",
    color: "slate",
    badge: "Settings",
    description:
      "Manage and review self-assignment requests from learners. Approve, deny, or suggest alternatives for course, certification, and learning path requests through a real-time chat interface.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Ticket Requests</strong> is the admin dashboard for managing self-assignment requests submitted by learners. Learners request access to courses, certifications, or learning paths, and admins approve, deny, or suggest alternatives through a built-in chat interface.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/c0853df0-28d1-488c-ba48-d059e4b316f4.png" alt="Ticket Requests List" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Ticket Requests list view</p></div>

      <section>
        <h2>Stats Cards</h2>
        <p>Four summary cards are displayed in the page header:</p>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Total Requests</strong></td>
              <td>Total number of ticket requests submitted.</td>
            </tr>
            <tr>
              <td><strong>Pending</strong></td>
              <td>Requests awaiting admin action.</td>
            </tr>
            <tr>
              <td><strong>Denied</strong></td>
              <td>Requests that have been denied.</td>
            </tr>
            <tr>
              <td><strong>Approved</strong></td>
              <td>Requests that have been approved and assigned.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Filters</h2>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Options</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Search</strong></td>
              <td>Search by ticket number, requester name, or request content.</td>
            </tr>
            <tr>
              <td><strong>Status</strong></td>
              <td>All Status, Pending, Approved, Denied, Closed.</td>
            </tr>
            <tr>
              <td><strong>Type</strong></td>
              <td>All Types, Course, Certification, Learning Path, General.</td>
            </tr>
            <tr>
              <td><strong>Approver</strong></td>
              <td>Filter by specific approver (searchable dropdown).</td>
            </tr>
            <tr>
              <td><strong>My Requests</strong></td>
              <td>Toggle button to show only tickets assigned to you. Saved in session.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Export</h2>
        <p>Two export buttons are available in the header to download ticket request data:</p>
        <table>
          <thead>
            <tr>
              <th>Format</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>CSV</strong></td>
              <td>Downloads ticket data as a CSV file with UTF-8 BOM for Excel compatibility. Includes CSV injection prevention.</td>
            </tr>
            <tr>
              <td><strong>Excel</strong></td>
              <td>Downloads ticket data as a native Excel (.xlsx) file with formatted columns.</td>
            </tr>
          </tbody>
        </table>
        <p>Exports respect all active filters (search, status, type, approver, and My Requests). Columns include: Ticket Number, Requester Name, Requester Email, Type, Requested For (dynamic columns for multi-item requests), Status, and Requested Date.</p>
      </section>

      <section>
        <h2>Ticket Table</h2>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Ticket No</strong></td>
              <td>Unique ticket number identifier.</td>
            </tr>
            <tr>
              <td><strong>Requester</strong></td>
              <td>Avatar, name, and email of the learner who submitted the request.</td>
            </tr>
            <tr>
              <td><strong>Type</strong></td>
              <td>Color-coded badge: Course (indigo), Certification (amber), Learning Path (green), General (purple).</td>
            </tr>
            <tr>
              <td><strong>Requested For</strong></td>
              <td>Item name(s) requested. Shows "+N more items" link for multiple items, or a truncated description for General requests.</td>
            </tr>
            <tr>
              <td><strong>Date</strong></td>
              <td>Submission date (dd/mm/yyyy).</td>
            </tr>
            <tr>
              <td><strong>Ticket Status</strong></td>
              <td>Pending (yellow), Approved (green), Denied (red), Closed (gray).</td>
            </tr>
            <tr>
              <td><strong>Action</strong></td>
              <td><strong>Chat</strong> button &mdash; opens the ticket detail/chat page.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Ticket Detail &amp; Chat</h2>
        <p>Clicking <strong>Chat</strong> opens a full-screen split view with a real-time messaging interface on the left and ticket details on the right.</p>

        <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/bf9f65b5-4bf5-45a5-952c-7ad4ac38442a.png" alt="Ticket Chat" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Ticket chat interface</p></div>

        <h3>Chat Features</h3>
        <table>
          <thead>
            <tr>
              <th>Feature</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Message thread</strong></td>
              <td>Real-time chat between admin and requester with sender avatars, timestamps, and read receipts (single tick, double tick, blue double tick).</td>
            </tr>
            <tr>
              <td><strong>Emoji reactions</strong></td>
              <td>React to messages with emojis via a hover picker.</td>
            </tr>
            <tr>
              <td><strong>Delete messages</strong></td>
              <td>Delete for everyone or delete for yourself.</td>
            </tr>
          </tbody>
        </table>

        <h3>Admin Actions (Right Sidebar)</h3>
        <table>
          <thead>
            <tr>
              <th>Status</th>
              <th>Available Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Pending</strong></td>
              <td><strong>Approve</strong> (assign items with deadline and notification settings), <strong>Deny</strong> (with required reason), or <strong>Suggest</strong> (for General tickets &mdash; search and suggest specific courses/certifications/learning paths).</td>
            </tr>
            <tr>
              <td><strong>Approved / Denied</strong></td>
              <td><strong>Close Ticket</strong> to finalize.</td>
            </tr>
            <tr>
              <td><strong>Closed</strong></td>
              <td>No further actions available.</td>
            </tr>
          </tbody>
        </table>
        <p>For General tickets, a <strong>Generate</strong> button lets admins navigate to the course/certification/learning path creator pre-filled with the ticket description.</p>
      </section>

      <section>
        <h2>Approve Settings</h2>
        <p>When approving a request, the admin configures:</p>
        <table>
          <thead>
            <tr>
              <th>Setting</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Mandatory</strong></td>
              <td>Mark the assignment as required completion.</td>
            </tr>
            <tr>
              <td><strong>Send Notification</strong></td>
              <td>Send email notification to the learner.</td>
            </tr>
            <tr>
              <td><strong>Deadline</strong></td>
              <td>Number of days to complete. Shows computed due date.</td>
            </tr>
            <tr>
              <td><strong>Automatic Reminders</strong></td>
              <td>Enable reminders at a configured frequency (every N days).</td>
            </tr>
            <tr>
              <td><strong>Message</strong></td>
              <td>Optional message to the learner.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Learner self-assignment request</div>
            <div class="sz-use-case-flow">
              A learner requests access to a specific certification. The admin opens the ticket chat, reviews the request details and approver list, and clicks Approve with a 30-day deadline and email notification enabled.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">General learning request</div>
            <div class="sz-use-case-flow">
              A learner submits a General request describing skills they want to develop. The admin clicks Suggest, searches for matching courses and learning paths, selects relevant items, and assigns them with a message explaining the recommendation.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Deny with feedback</div>
            <div class="sz-use-case-flow">
              An admin denies a request that doesn't align with the learner's role, providing a clear reason. The learner sees the denial reason and can submit a new request with updated justification.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Pro tip</div>
        <p>Use the <strong>My Requests</strong> toggle to quickly filter tickets assigned to you as an approver, and use the chat interface to discuss requirements before approving or denying.</p>
      </div>

    </div>`,
  },

  "webhooks": {
    title: "Webhook Configuration",
    subtitle: "Event-Driven Integration",
    color: "slate",
    badge: "Settings",
    description:
      "Configure webhooks to send HTTP notifications to external systems when events occur. Set up endpoint URLs, choose trigger events, configure authentication, retry policies, and monitor delivery history.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Webhook Configuration</strong> sends HTTP notifications to your external systems when events occur in the platform. Configure endpoint URLs, select trigger events, set up API key authentication, and monitor delivery history with detailed logs.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/c3b83065-9621-4a36-a343-5c7bed452c3c.png" alt="Webhooks" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Webhook configuration</p></div>

      <section>
        <h2>Stats Cards</h2>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Total Webhooks</strong></td><td>Total configured webhooks.</td></tr>
            <tr><td><strong>Active</strong></td><td>Currently enabled webhooks.</td></tr>
            <tr><td><strong>Failed</strong></td><td>Webhooks with delivery failures.</td></tr>
            <tr><td><strong>Total Calls</strong></td><td>Sum of all webhook calls across all webhooks.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Webhook List</h2>
        <p>Each webhook is shown as a card with:</p>
        <table>
          <thead>
            <tr>
              <th>Element</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Name &amp; Status</strong></td><td>Webhook name with status icon (green = active, red = inactive, alert = has errors).</td></tr>
            <tr><td><strong>HTTP Method</strong></td><td>Color-coded badge: GET (blue), POST (green), PUT (yellow), PATCH (orange), DELETE (red).</td></tr>
            <tr><td><strong>HTTPS Indicator</strong></td><td>Green shield icon for secure endpoints.</td></tr>
            <tr><td><strong>Trigger Events</strong></td><td>Blue tags showing which events trigger this webhook.</td></tr>
            <tr><td><strong>Endpoint URL</strong></td><td>Full URL with copy-to-clipboard button.</td></tr>
            <tr><td><strong>API Key Config</strong></td><td>Shows format (header/query/body) and key name. Value is encrypted (toggle eye icon to reveal first 20 chars).</td></tr>
            <tr><td><strong>Metrics</strong></td><td>Last triggered time, total calls, and success rate.</td></tr>
          </tbody>
        </table>

        <h3>Filters</h3>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Options</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Search</strong></td><td>Search by URL, description, name, or trigger event.</td></tr>
            <tr><td><strong>Status</strong></td><td>All Status, Active, Inactive, Has Errors.</td></tr>
          </tbody>
        </table>

        <h3>Actions Per Webhook</h3>
        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Test Webhook</strong></td><td>Send a test payload to the endpoint. Disabled if webhook is inactive.</td></tr>
            <tr><td><strong>View History</strong></td><td>Open execution history modal with status, HTTP code, response time, retry attempts, payload, and response.</td></tr>
            <tr><td><strong>Edit</strong></td><td>Modify webhook configuration.</td></tr>
            <tr><td><strong>Delete</strong></td><td>Remove with confirmation.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Create / Edit Webhook</h2>

        <h3>Basic Configuration</h3>
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Webhook Name</strong></td><td>Required, minimum 3 characters.</td></tr>
            <tr><td><strong>Description</strong></td><td>Optional description of the webhook's purpose.</td></tr>
            <tr><td><strong>Trigger Events</strong></td><td>Multi-select checkboxes organized by event category.</td></tr>
            <tr><td><strong>Endpoint URL</strong></td><td>Target URL (http/https required).</td></tr>
            <tr><td><strong>HTTP Method</strong></td><td>GET, POST, PUT, PATCH, or DELETE.</td></tr>
          </tbody>
        </table>

        <h3>API Key Authentication</h3>
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>API Key Format</strong></td><td>How to send the key: Header, Query parameter, or Body.</td></tr>
            <tr><td><strong>API Key Name</strong></td><td>Header/parameter name (e.g., "X-API-Key", "Authorization").</td></tr>
            <tr><td><strong>API Key Value</strong></td><td>Secret value (encrypted in storage).</td></tr>
          </tbody>
        </table>

        <h3>Advanced Configuration</h3>
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Content Type</strong></td><td>JSON, XML, or Form URL Encoded.</td></tr>
            <tr><td><strong>Priority</strong></td><td>Low, Medium, High, or Critical.</td></tr>
            <tr><td><strong>Retry</strong></td><td>Enable with max attempts (1&ndash;10, default 3) and backoff seconds (10&ndash;300s, default 60s).</td></tr>
            <tr><td><strong>Timeout</strong></td><td>5&ndash;120 seconds (default 30).</td></tr>
            <tr><td><strong>HMAC Signature</strong></td><td>Enable to include a signature header for payload integrity verification. Configure secret and header name (default: "X-Webhook-Signature").</td></tr>
            <tr><td><strong>Status</strong></td><td>Active or Inactive.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Execution History</h2>
        <p>The history modal shows delivery records with:</p>
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Status</strong></td><td>SUCCESS (green), FAILED (red), PENDING (yellow), TIMEOUT (orange), RETRYING (blue).</td></tr>
            <tr><td><strong>HTTP Status Code</strong></td><td>Color-coded (200&ndash;299 = green, others = red).</td></tr>
            <tr><td><strong>Response Time</strong></td><td>Duration in milliseconds.</td></tr>
            <tr><td><strong>Retry Attempts</strong></td><td>Attempt number if retried.</td></tr>
            <tr><td><strong>Payload</strong></td><td>JSON preview of the sent data.</td></tr>
            <tr><td><strong>Response</strong></td><td>JSON preview of the received response.</td></tr>
            <tr><td><strong>Error Message</strong></td><td>Displayed if the delivery failed.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">HR system integration</div>
            <div class="sz-use-case-flow">
              Create a webhook pointing to your HRIS endpoint. Select course completion and certification events as triggers. Set API Key authentication with your HRIS API key. Enable retry for reliability.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Slack notifications</div>
            <div class="sz-use-case-flow">
              Create a webhook with your Slack incoming webhook URL. Select achievement and completion events. Set content type to JSON. Test the webhook to verify messages appear in your Slack channel.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Use the <strong>Test Webhook</strong> button to verify your endpoint receives payloads correctly before enabling. Enable <strong>HMAC Signature</strong> for production webhooks to verify payload integrity.</p>
      </div>

    </div>`,
  },
};
